/**
 * Auth Context — contexto de autenticação PPM.PAGO (spec §11.9)
 *
 * LOGIN REAL (W0-09): quando VITE_SUPABASE_URL/VITE_SUPABASE_ANON_KEY estão
 * configurados, a SPA autentica DIRETO no Supabase Auth (signInWithPassword —
 * habilita o fluxo nativo de MFA/TOTP do GoTrue). O access_token resultante é
 * um JWT Supabase genuíno, validado pelo backend pelo MESMO Verifier das
 * rotas protegidas (HS256/JWKS — nenhuma troca de token no Go). O par de
 * tokens é entregue a POST /bff/auth/session só para custodiar o
 * refresh_token em cookie HttpOnly (nunca em localStorage — spec §11.9); o
 * refresh subsequente usa POST /bff/auth/refresh (já implementado em
 * token-store.ts/api-client.ts).
 *
 * LOGIN DE DESENVOLVIMENTO (fallback, W1-34): sem credenciais Supabase
 * configuradas, a SPA cai em POST /bff/auth/dev-login — o backend Go emite um
 * JWT HS256 REAL (mesmo SUPABASE_JWT_SECRET que valida as rotas) para
 * usuários semeados (ver backend devUsuarios): operador@ppm.dev (aal1),
 * analista-financeiro@ppm.dev (aal2, configura cobrança/inadimplência — #611),
 * operador-mfa@ppm.dev (aal2), admin@ppm.dev (aal2). Senha: "dev". Endpoint
 * inexistente em produção (gate no backend).
 *
 * MFA/TOTP (feat/mfa-totp): o enrollment (cadastro do fator TOTP) mora em
 * /perfil (features/perfil/perfil-page.tsx) e o step-up (challenge/verify
 * quando uma ação crítica retorna 403 MFA_REQUERIDA) mora no
 * MfaConfirmDialog (features/shared/mfa-confirm-dialog.tsx), reusado nas
 * páginas com ações críticas. Ambos usam @supabase/supabase-js
 * (auth.mfa.enroll/listFactors/challengeAndVerify/unenroll) e sincronizam a
 * sessão elevada (aal2) com o BFF/token-store via @/lib/mfa-session-sync —
 * mesmo padrão de custódia do refresh_token usado aqui em loginReal. Sem
 * Supabase real configurado (login de desenvolvimento), não há como fazer
 * step-up/enrollment: a UI avisa isso honestamente em vez de fingir.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import {
  setAccessToken,
  clearAccessToken,
  getAccessToken,
  getTokenPayload,
  isTokenExpired,
  refreshAccessToken,
} from '@/store/token-store'
import { getSupabaseClient, supabaseAuthDisponivel } from '@/lib/supabase-client'
import { useDevSessionStore } from './dev-session-store'
import { definirPermissoesDaSessao } from './permissions'

export interface AuthUser {
  id: string
  email: string
  nome: string
  role: string
  /** Perfis RBAC (dica de UX p/ disable-por-papel; backend é a fronteira real). */
  perfis: string[]
  /** GET /bff/auth/me falhou: `perfis` vazio aqui NÃO quer dizer conta sem acesso (#828). */
  perfisIndisponiveis?: boolean
  /** Permissões efetivas (matriz do código + ajustes do banco, #897). Ausente ⇒ temPermissao cai na MATRIZ. */
  permissoes?: string[]
  /** #827: conta com senha provisória — a SPA leva a /definir-senha antes de tudo. */
  senhaProvisoria?: boolean
  senhaProvisoriaExpirada?: boolean
}

export interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** true enquanto o login for o de desenvolvimento (JWT real, usuários semeados). */
  isDevLogin: boolean
  /** Desliga a marca local depois de POST /v1/me/senha-definida. */
  marcarSenhaDefinida: () => void
  /** Rebusca /bff/auth/me após um ajuste RBAC (#897); falha mantém a sessão como estava. */
  atualizarSessao: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Mesma base da api-client: vazio = mesma origem (proxy do Vite → :8080).
const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''
const DEV_LOGIN_ENDPOINT = `${API_BASE_URL}/bff/auth/dev-login`
const BFF_SESSION_ENDPOINT = `${API_BASE_URL}/bff/auth/session`
const BFF_LOGOUT_ENDPOINT = `${API_BASE_URL}/bff/auth/logout`
const BFF_ME_ENDPOINT = `${API_BASE_URL}/bff/auth/me`
const BFF_REFRESH_ENDPOINT = `${API_BASE_URL}/bff/auth/refresh`
const ME_SENHA_ENDPOINT = `${API_BASE_URL}/v1/me/senha`

// Mesma frase nos dois caminhos de login: quem opera não precisa saber se a
// recusa veio do provedor de identidade ou do atalho de desenvolvimento.
export const AVISO_ACESSO_ENCERRADO =
  'Seu acesso foi encerrado. Procure quem administra a plataforma.'

interface DevLoginResponse {
  access_token: string
}

interface BffMeResponse {
  perfis: string[]
  permissoes?: string[]
  carteira_restrita?: boolean
}

interface Sessao {
  perfis: string[]
  /** null quando o campo não veio (ex.: backend antigo durante o deploy). */
  permissoes: string[] | null
  /** Ausente (backend antigo) = global: o servidor continua recortando de qualquer forma. */
  carteiraRestrita?: boolean
}

// O JWT do Supabase não traz perfis: /bff/auth/me devolve perfis e permissões
// efetivas (#897). Falha devolve null — perfisIndisponiveis e MATRIZ estática.
async function fetchSessao(accessToken: string): Promise<Sessao | null> {
  try {
    const response = await fetch(BFF_ME_ENDPOINT, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    })
    if (!response.ok) return null
    const data = (await response.json()) as BffMeResponse
    return {
      perfis: data.perfis ?? [],
      permissoes: data.permissoes ?? null,
      carteiraRestrita: data.carteira_restrita,
    }
  } catch {
    return null
  }
}

// Permissões efetivas e veredito de carteira restrita (FINC-12) andam juntos.
const aplicarSessao = (s: Sessao | null) => definirPermissoesDaSessao(s?.permissoes ?? null, s?.carteiraRestrita)

interface EstadoSenha {
  senhaProvisoria: boolean
  senhaProvisoriaExpirada: boolean
}

// Falha aqui não trava a tela: a trava de verdade é o backend, que responde
// 403 SENHA_PROVISORIA em /v1 enquanto a marca estiver ligada.
async function buscarEstadoSenha(accessToken: string): Promise<EstadoSenha> {
  try {
    const response = await fetch(ME_SENHA_ENDPOINT, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    })
    if (!response.ok) return { senhaProvisoria: false, senhaProvisoriaExpirada: false }
    const data = (await response.json()) as { senha_provisoria?: boolean; senha_provisoria_expirada?: boolean }
    return {
      senhaProvisoria: data.senha_provisoria === true,
      senhaProvisoriaExpirada: data.senha_provisoria_expirada === true,
    }
  } catch {
    return { senhaProvisoria: false, senhaProvisoriaExpirada: false }
  }
}

// ─── Login real (Supabase Auth) ──────────────────────────────────────────────

async function loginReal(email: string, password: string): Promise<void> {
  const supabase = getSupabaseClient()
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  })

  if (error || !data.session) {
    throw new Error(
      error?.message === 'Invalid login credentials'
        ? 'E-mail ou senha inválidos.'
        : (error?.message ?? 'Não foi possível entrar. Tente novamente.'),
    )
  }

  const { access_token, refresh_token } = data.session

  // Custodia o refresh_token em cookie HttpOnly ANTES de considerar o login
  // bem-sucedido: sem isto, a sessão "funciona" até o access_token expirar e
  // então cai sem aviso (sem refresh possível).
  const sessionResponse = await fetch(BFF_SESSION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ access_token, refresh_token }),
  }).catch(() => null)

  if (!sessionResponse || !sessionResponse.ok) {
    await supabase.auth.signOut().catch(() => undefined)
    if (sessionResponse?.status === 403) {
      const corpo = (await sessionResponse.json().catch(() => null)) as { code?: string } | null
      if (corpo?.code === 'USUARIO_INATIVO') {
        throw new Error(AVISO_ACESSO_ENCERRADO)
      }
    }
    throw new Error('Não foi possível estabelecer a sessão no servidor. Tente novamente.')
  }

  setAccessToken(access_token)
}

// ─── Login de desenvolvimento (fallback sem Supabase) ────────────────────────

async function loginDev(email: string, password: string): Promise<void> {
  const response = await fetch(DEV_LOGIN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email: email.trim(), senha: password }),
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { code?: string; message?: string }
    if (body.code === 'USUARIO_INATIVO') {
      throw new Error(AVISO_ACESSO_ENCERRADO)
    }
    throw new Error(
      body.message ?? (response.status === 401
        ? 'E-mail ou senha inválidos.'
        : 'Não foi possível entrar. Verifique se a API (:8080) está no ar.'),
    )
  }

  const data = (await response.json()) as DevLoginResponse
  setAccessToken(data.access_token)
  useDevSessionStore.getState().setAccessToken(data.access_token)
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // useContext e não useQueryClient: este provider também roda fora de um
  // QueryClientProvider (testes do próprio auth), e useQueryClient lança.
  const queryClient = useContext(QueryClientContext)

  // Re-hidratação da sessão no boot (ex.: F5). O access_token vive só em
  // memória (spec §11.9) e é perdido a cada recarga; no login REAL o
  // refresh_token fica em cookie HttpOnly (custodiado por /bff/auth/session).
  // Então ao montar tentamos reemitir o access_token a partir do cookie via
  // /bff/auth/refresh: sucesso ⇒ restaura a sessão sem novo login; falha
  // (cookie ausente/expirado) ⇒ segue deslogado. No dev-login não há cookie
  // (devauth.go não tem refresh) — a rehidratação usa o token persistido em
  // dev-session-store.ts (localStorage), escopo exclusivo do dev-login.
  useEffect(() => {
    let cancelado = false

    async function reidratarSessao() {
      if (!supabaseAuthDisponivel) {
        const tokenDev = useDevSessionStore.getState().accessToken
        if (!tokenDev) {
          if (!cancelado) setIsLoading(false)
          return
        }
        setAccessToken(tokenDev)
        const payload = getTokenPayload()
        if (isTokenExpired() || !payload) {
          clearAccessToken()
          useDevSessionStore.getState().setAccessToken(null)
          definirPermissoesDaSessao(null)
          if (!cancelado) setIsLoading(false)
          return
        }
        const [sessaoDev, senhaDev] = await Promise.all([fetchSessao(tokenDev), buscarEstadoSenha(tokenDev)])
        if (!cancelado) {
          // No dev-login só as permissões vêm do /me; os perfis seguem do JWT.
          aplicarSessao(sessaoDev)
          setUser({
            id: payload.sub,
            email: payload.email ?? '',
            nome: payload.email ?? '',
            role: payload.role,
            perfis: payload.app_metadata?.ppm_perfis ?? [],
            permissoes: sessaoDev?.permissoes ?? undefined,
            ...senhaDev,
          })
          setIsLoading(false)
        }
        return
      }

      const token = await refreshAccessToken(BFF_REFRESH_ENDPOINT)
      if (cancelado) return

      const payload = getTokenPayload()
      if (!token || !payload) {
        definirPermissoesDaSessao(null)
        setIsLoading(false)
        return
      }

      const [sessao, senha] = await Promise.all([fetchSessao(token), buscarEstadoSenha(token)])
      if (cancelado) return

      aplicarSessao(sessao)
      setUser({
        id: payload.sub,
        email: payload.email ?? '',
        nome: payload.email ?? '',
        role: payload.role,
        perfis: sessao?.perfis ?? [],
        perfisIndisponiveis: sessao === null,
        permissoes: sessao?.permissoes ?? undefined,
        ...senha,
      })
      setIsLoading(false)
    }

    void reidratarSessao()

    return () => {
      cancelado = true
    }
  }, [])

  // Computador compartilhado: a sessão anterior pode ter caído sem "Sair"
  // (token expirado, navegador fechado) e deixado o navegador inscrito para
  // outra pessoa. A cada pessoa nova na sessão — login ou reidratação —, o
  // servidor diz de quem é a inscrição e, se não for desta, ela sai daqui.
  // Import sob demanda, sem esperar: nunca segura a entrada.
  const usuarioId = user?.id
  // A limpeza cancela a nova tentativa da conferência quando a pessoa sai ou muda.
  useEffect(() => {
    if (!usuarioId) return
    let vigente = true
    void import('@/services/push-api')
      .then((m) => {
        if (vigente) return m.soltarInscricaoDeOutraPessoa()
      })
      .catch(() => undefined)
    return () => {
      vigente = false
      void import('@/services/push-api').then((m) => m.cancelarConferenciaPush()).catch(() => undefined)
    }
  }, [usuarioId])

  const login = useCallback(async (email: string, password: string) => {
    if (supabaseAuthDisponivel) {
      await loginReal(email, password)
    } else {
      await loginDev(email, password)
    }

    const payload = getTokenPayload()
    if (!payload) {
      clearAccessToken()
      throw new Error('Token inválido recebido do servidor.')
    }

    // Perfis (RBAC) são só uma dica de UX (disable-por-papel); a fronteira
    // real é o backend, que resolve de usuario_perfil a cada requisição.
    // No dev-login o JWT já embute app_metadata.ppm_perfis; no login real
    // (Supabase) esse claim nunca existe — GET /bff/auth/me busca a mesma
    // fonte (usuario_perfil) que o middleware já resolve por requisição.
    // Permissões efetivas (#897) vêm sempre de /bff/auth/me, dev ou real.
    const token = getAccessToken() ?? ''
    const [sessao, senha] = await Promise.all([fetchSessao(token), buscarEstadoSenha(token)])
    const perfis = supabaseAuthDisponivel ? (sessao?.perfis ?? null) : (payload.app_metadata?.ppm_perfis ?? [])

    // Sessão anterior pode ter caído sem logout (token expirado): o cache dela
    // não pode aparecer para quem entra agora (#828).
    queryClient?.clear()
    aplicarSessao(sessao)
    setUser({
      id: payload.sub,
      email: payload.email || email.trim(),
      nome: payload.email || email.trim(),
      role: payload.role,
      perfis: perfis ?? [],
      perfisIndisponiveis: perfis === null,
      permissoes: sessao?.permissoes ?? undefined,
      ...senha,
    })
  }, [queryClient])

  const marcarSenhaDefinida = useCallback(() => {
    setUser((atual) => atual && { ...atual, senhaProvisoria: false, senhaProvisoriaExpirada: false })
  }, [])

  // sessao === null não mexe no usuário nem nas permissões: vale a sessão que já estava.
  const atualizarSessao = useCallback(async () => {
    const token = getAccessToken()
    if (!token) return
    const sessao = await fetchSessao(token)
    if (sessao === null) return
    aplicarSessao(sessao)
    setUser((atual) => {
      if (!atual) return atual
      if (!supabaseAuthDisponivel) {
        return { ...atual, permissoes: sessao.permissoes ?? undefined }
      }
      return {
        ...atual,
        perfis: sessao.perfis,
        perfisIndisponiveis: false,
        permissoes: sessao.permissoes ?? undefined,
      }
    })
  }, [])

  const logout = useCallback(async () => {
    // Antes de derrubar o token: o navegador para de receber as notificações
    // desta pessoa (outra pode entrar nele). Import sob demanda para não pesar
    // no carregamento inicial; no máximo 3 s, e nunca impede a saída.
    await Promise.race([
      import('@/services/push-api').then((m) => m.sairDoPushNesteAparelho()).catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ])
    if (supabaseAuthDisponivel) {
      try {
        await getSupabaseClient().auth.signOut()
      } catch {
        // Best-effort: mesmo se o signOut remoto falhar, a sessão local cai.
      }
      try {
        await fetch(BFF_LOGOUT_ENDPOINT, { method: 'POST', credentials: 'include' })
      } catch {
        // Best-effort: o cookie httpOnly expira sozinho se isto falhar.
      }
    }
    clearAccessToken()
    useDevSessionStore.getState().setAccessToken(null)
    definirPermissoesDaSessao(null)
    setUser(null)
    // Sem isto o próximo login na mesma aba via o dado desta sessão, mesmo com
    // o backend recusando a consulta (#828).
    queryClient?.clear()
  }, [queryClient])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
        isDevLogin: !supabaseAuthDisponivel,
        marcarSenhaDefinida,
        atualizarSessao,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

// Para componentes que também montam fora do AuthProvider: sem sessão, null.
export function useAuthOpcional(): AuthContextValue | null {
  return useContext(AuthContext)
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  return ctx
}
